import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useApp } from '../context/AppContext';

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ClearIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export function SearchInput() {
  const { data, selectState } = useApp();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  
  // Filter states based on query
  const filteredStates = useMemo(() => {
    if (!data || !query.trim()) return [];
    
    const lowerQuery = query.toLowerCase();
    return data.states
      .filter((state) => state.toLowerCase().includes(lowerQuery))
      .slice(0, 8); // Limit to 8 results
  }, [data, query]);
  
  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
    setFocusedIndex(-1);
  }, []);
  
  // Handle state selection
  const handleSelect = useCallback((state: string) => {
    selectState(state);
    setQuery('');
    setIsOpen(false);
    setFocusedIndex(-1);
    inputRef.current?.blur();
  }, [selectState]);
  
  // Clear input
  const handleClear = useCallback(() => {
    setQuery('');
    setFocusedIndex(-1);
    inputRef.current?.focus();
  }, []);
  
  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || filteredStates.length === 0) {
      if (e.key === 'ArrowDown' && query) {
        setIsOpen(true);
      }
      return;
    }
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => 
          prev < filteredStates.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => 
          prev > 0 ? prev - 1 : filteredStates.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredStates.length) {
          handleSelect(filteredStates[focusedIndex]);
        } else if (filteredStates.length === 1) {
          handleSelect(filteredStates[0]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  }, [isOpen, filteredStates, focusedIndex, query, handleSelect]);
  
  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[focusedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);
  
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.parentElement?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative">
      {/* Input */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500">
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setIsOpen(true)}
          placeholder="Search states..."
          className="input pl-10 pr-10"
          aria-label="Search for a state"
          aria-expanded={isOpen}
          aria-controls="search-results"
          aria-autocomplete="list"
          role="combobox"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-300 transition-colors"
            aria-label="Clear search"
          >
            <ClearIcon />
          </button>
        )}
      </div>
      
      {/* Dropdown */}
      {isOpen && filteredStates.length > 0 && (
        <ul
          ref={listRef}
          id="search-results"
          role="listbox"
          className="absolute z-50 w-full mt-1 py-1 bg-surface-800 border border-surface-600 rounded-lg shadow-xl max-h-64 overflow-y-auto"
        >
          {filteredStates.map((state, index) => (
            <li
              key={state}
              role="option"
              aria-selected={index === focusedIndex}
              className={`
                px-4 py-2 cursor-pointer transition-colors
                ${index === focusedIndex 
                  ? 'bg-accent-500/20 text-accent-400' 
                  : 'text-surface-200 hover:bg-surface-700'
                }
              `}
              onClick={() => handleSelect(state)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              {/* Highlight matching text */}
              {highlightMatch(state, query)}
            </li>
          ))}
        </ul>
      )}
      
      {/* No results */}
      {isOpen && query && filteredStates.length === 0 && (
        <div className="absolute z-50 w-full mt-1 p-4 bg-surface-800 border border-surface-600 rounded-lg shadow-xl text-center text-surface-400">
          No states found for "{query}"
        </div>
      )}
    </div>
  );
}

// Helper to highlight matching text
function highlightMatch(text: string, query: string) {
  if (!query) return text;
  
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  
  if (index === -1) return text;
  
  return (
    <>
      {text.slice(0, index)}
      <span className="font-bold text-accent-400">
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
}

